import {
	SpanKind,
	SpanStatusCode,
	type Span,
	type Tracer,
} from "@opentelemetry/api";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-proto";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { requestSpanPath } from "./requestSpanName";
import {
	browserRequestContext,
	serverRequestContext,
	type RequestLogProperties,
} from "./requestLogContext";

let tracer: Tracer | undefined;
let provider: WebTracerProvider | undefined;
let apiBase: URL | undefined;
let applicationProperties: RequestLogProperties = {};
let getErrorContext: (() => RequestLogProperties) | undefined;
let confirmedContext: { key: string; properties: RequestLogProperties; recordedAt: number } | undefined;
const reportedErrors = new WeakSet<object>();
let errorWindowStart = 0;
let errorWindowCount = 0;

function identityKey(properties: RequestLogProperties) {
	return JSON.stringify([properties.RequesterId ?? properties.Requester, properties.RequesterTenantId,
		properties.HostMode, properties.ViewMode, properties.ImpersonatedUser]);
}

export function requestOutcome(status?: number, canceled = false, failed = false) {
	return canceled ? { Outcome: "Canceled", Severity: "Information" } :
		status !== undefined && status >= 500 ? { Outcome: "ServerError", Severity: "Error" } :
		status !== undefined && status >= 400 ? { Outcome: "ClientError", Severity: "Warning" } :
		failed ? { Outcome: "NetworkError", Severity: "Error" } : { Outcome: "Success", Severity: "Information" };
}

// Capture actual errors, including React errors handled by a boundary. These
// use the existing bounded OTLP span queue and authenticated telemetry relay.
export function reportFrontendError(error: unknown, source: string, componentStack?: string) {
	try {
		if (!tracer) return;
		if (error !== null && typeof error === "object") {
			if (reportedErrors.has(error)) return;
			reportedErrors.add(error);
		}
		const now = Date.now();
		if (now - errorWindowStart >= 60000) { errorWindowStart = now; errorWindowCount = 0; }
		if (++errorWindowCount > 20) return;
		let context: RequestLogProperties = {};
		try { context = getErrorContext?.() ?? {}; } catch { /* Authentication may not be initialized yet. */ }
		const properties = confirmedContext?.key === identityKey(context) && now - confirmedContext.recordedAt < 120000
			? { ...confirmedContext.properties, RequestContextSource: "ServerSnapshot" } : context;
		const { CorrelationId: _correlation, RequestMethod: _method, RequestPath: _path, ...identity } = properties;
		const sanitize = (value: string, limit = 2048) => value
			.replace(/Bearer\s+[^\s]+/gi, "Bearer [redacted]")
			.replace(/https?:\/\/[^\s)]+/g, value => {
				try { const url = new URL(value); return url.origin + url.pathname; } catch { return "[url]"; }
			}).slice(0, limit);
		// HTTP error response bodies are deliberately excluded, even when wrapped
		// by a query boundary or rejected promise.
		const httpError = typeof error === "object" && error !== null && "isAxiosError" in error;
		const message = httpError ? "HTTP request failed" : error instanceof Error ? error.message : typeof error === "string" ? error : "Non-Error rejection";
		const span = tracer.startSpan(`Frontend error: ${source}`, { kind: SpanKind.INTERNAL,
			attributes: { ...applicationProperties, ...identity, EventType: "FrontendError", ErrorSource: source,
				Severity: "Error", Outcome: "Error", PagePath: window.location.pathname,
				ExceptionType: error instanceof Error ? error.name : "Unknown", "error.type": error instanceof Error ? error.name : "Unknown", ErrorMessage: sanitize(message, 1024),
				...(error instanceof Error && !httpError ? { StackTrace: sanitize(error.stack ?? "", 4096) } : {}),
				...(componentStack ? { ComponentStack: sanitize(componentStack) } : {}),
			} });
		span.setStatus({ code: SpanStatusCode.ERROR });
		span.end();
	} catch { /* Error reporting must never cause another application error. */ }
}

export const reactErrorCallbacks = {
	onCaughtError: (error: unknown, info: { componentStack?: string | null }) => {
		console.error(error);
		reportFrontendError(error, "React", info.componentStack ?? undefined);
	},
	onUncaughtError: (error: unknown, info: { componentStack?: string | null }) => {
		console.error(error);
		reportFrontendError(error, "React", info.componentStack ?? undefined);
	},
	onRecoverableError: (error: unknown, info: { componentStack?: string | null }) => {
		console.error(error);
		reportFrontendError(error, "ReactRecoverable", info.componentStack ?? undefined);
	},
};
const requests = new WeakMap<
	InternalAxiosRequestConfig,
	{ span: Span; properties: RequestLogProperties; started: number }
>();

export function initializeBrowserTracing(options: {
	apiBaseUrl: string;
	getHeaders: () => Promise<Record<string, string>>;
	environment: string;
	version: string;
	buildNumber?: string;
	getErrorContext?: () => RequestLogProperties;
	enabled: boolean;
}) {
	if (!options.enabled || provider) return;
	getErrorContext = options.getErrorContext;
	applicationProperties = {
		Application: "Portals.Frontend",
		Environment: ({ dev: "Development", development: "Development", prod: "Production", production: "Production", test: "Test" } as Record<string, string>)[options.environment.toLowerCase()] ?? options.environment,
		ApplicationVersion: options.version,
		BuildNumber: options.buildNumber ?? "local",
		"service.version": options.version,
	};
	apiBase = new URL(
		options.apiBaseUrl.replace(/\/$/, "") + "/api/",
		window.location.origin,
	);
	provider = new WebTracerProvider({
		spanLimits: { attributeValueLengthLimit: 4096 },
		resource: resourceFromAttributes({
			"service.name": "Portals.Frontend",
			...applicationProperties,
		}),
		spanProcessors: [
			new BatchSpanProcessor(
				new OTLPTraceExporter({
					url: new URL("telemetry/traces", apiBase).href,
					headers: async () => {
						try {
							return await options.getHeaders();
						} catch {
							return {};
						} // No login popup or application failure for telemetry.
					},
					timeoutMillis: 5000,
					concurrencyLimit: 1,
				}),
				{
					maxQueueSize: 256,
					maxExportBatchSize: 32,
					scheduledDelayMillis: 5000,
					exportTimeoutMillis: 5000,
				},
			),
		],
	});
	tracer = provider.getTracer("portals-api-client");
	window.addEventListener("error", event => {
		if (event instanceof ErrorEvent) reportFrontendError(event.error ?? event.message, "JavaScript");
	});
	window.addEventListener("unhandledrejection", event => reportFrontendError(event.reason, "UnhandledPromise"));
	// Best effort on navigation; never block navigation on telemetry delivery.
	window.addEventListener("pagehide", () => {
		void provider?.forceFlush().catch(() => {});
	});
}

export function instrumentPortalClient(client: AxiosInstance) {
	// Register before auth interceptors: Axios executes request interceptors in
	// reverse order, so this sees the final authenticated URL immediately before dispatch.
	client.interceptors.request.use((config) => {
		try {
			if (!tracer || !apiBase) return config;
			let url: URL;
			try {
				url = new URL(client.getUri(config), window.location.origin);
			} catch {
				return config;
			}
			if (
				url.origin !== apiBase.origin ||
				!url.pathname.startsWith(apiBase.pathname) ||
				url.pathname.startsWith(apiBase.pathname + "telemetry/") ||
				url.pathname.startsWith(apiBase.pathname + "health")
			)
				return config;

			const method = config.method?.toUpperCase() ?? "GET";
			const properties = browserRequestContext(config, url, apiBase.pathname);
			const span = tracer.startSpan(
				`${method} ${requestSpanPath(url.pathname, apiBase.pathname)}`,
				{
					kind: SpanKind.CLIENT,
					attributes: {
						...applicationProperties,
						EventType: "HttpRequestCompleted",
						"http.request.method": method,
						"url.full": url.origin + url.pathname,
						"server.address": url.hostname,
					},
				},
			);
			const context = span.spanContext();
			config.headers.set(
				"traceparent",
				`00-${context.traceId}-${context.spanId}-${context.traceFlags.toString(16).padStart(2, "0")}`,
			);
			// Do not retain a caller's tracestate for the fresh span we created.
			config.headers.delete("tracestate");
			requests.set(config, { span, properties, started: performance.now() });
			return config;
		} catch {
			return config;
		} // Telemetry must never prevent an API call.
	});
	const finish = (
		config: InternalAxiosRequestConfig | undefined,
		status?: number,
		failed = false,
		headers?: Record<string, unknown>,
		canceled = false,
	) => {
		if (!config) return;
		const request = requests.get(config);
		if (!request) return;
		const { span } = request;
		requests.delete(config);
		const authoritative = serverRequestContext(
			headers?.["x-portal-log-context"],
		);
		span.setAttributes(authoritative ?? request.properties);
		if (authoritative && (request.properties.RequesterId || request.properties.Requester))
			confirmedContext = { key: identityKey(request.properties), properties: authoritative, recordedAt: Date.now() };
		const correlation = headers?.["x-correlation-id"];
		if (typeof correlation === "string")
			span.setAttribute("CorrelationId", correlation);
		span.setAttribute(
			"ElapsedMilliseconds",
			performance.now() - request.started,
		);
		const requestCanceled = canceled || config.signal?.aborted === true;
		span.setAttribute("RequestCanceled", requestCanceled);
		span.setAttributes(requestOutcome(status, requestCanceled, failed));
		if (status !== undefined)
			span.setAttributes({
				"http.response.status_code": status,
				StatusCode: status,
			});
		if (failed || (status !== undefined && status >= 400)) {
			span.setStatus({ code: SpanStatusCode.ERROR });
			span.setAttribute(
				"error.type",
				requestCanceled ? "canceled" : status === undefined ? "network" : String(status),
			);
		}
		// Error messages can contain URLs or response bodies, so record classification only.
		span.end();
	};
	client.interceptors.response.use(
		(response) => {
			try {
				finish(response.config, response.status, false, response.headers);
			} catch {}
			return response;
		},
		(error) => {
			try {
				finish(
					error?.config,
					error?.response?.status,
					true,
					error?.response?.headers,
					error?.code === "ERR_CANCELED",
				);
			} catch {}
			return Promise.reject(error);
		},
	);
}
